import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API_BASE_URL from "../services/apiClient";

const PublicTrip = () => {
  const navigate = useNavigate();
  const { shareToken } = useParams();

  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  /* ================= FETCH TRIP ================= */

  useEffect(() => {

    if (!shareToken) {
      setError("Invalid share link");
      setLoading(false);
      return;
    }

    const fetchTrip = async () => {

      try {

        const res = await fetch(
          `${API_BASE_URL}/api/trips/share/${shareToken}`
        );

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Trip not found");
        }

        setTrip(data);

      } catch (err) {

        console.error(err);
        setError("This trip is unavailable or expired.");

      } finally {

        setLoading(false);

      }

    };

    fetchTrip();

  }, [shareToken]);

  useEffect(() => {

    const autoCopyTrip = async () => {

      const redirect = localStorage.getItem("redirectAfterLogin");

      if (!redirect) return;

      if (!redirect.includes(`/share/${shareToken}`)) return;

      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      if (!token) return;

      try {

        setSaving(true);

        const res = await fetch(
          `${API_BASE_URL}/api/trips/share/${shareToken}/copy`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        const data = await res.json();

        if (!res.ok) {
          alert(data.message || "Failed to save trip");
          setSaving(false);
          return;
        }

        localStorage.removeItem("redirectAfterLogin");

        navigate(`/trip/${data.travelKeyToken}`);

      } catch (err) {

        console.error("Auto copy failed", err);

      } finally {

        setSaving(false);

      }

    };

    autoCopyTrip();

  }, [shareToken, navigate]);


  /* ================= COPY TRIP ================= */



  const handleSaveTrip = async () => {

    if (saving) return;

    setSaving(true);

    const authToken =
      localStorage.getItem("token") ||
      sessionStorage.getItem("token");

    if (!authToken) {

      localStorage.setItem(
        "redirectAfterLogin",
        window.location.href
      );

      setSaving(false);

      navigate("/?login=true");

      return;
    }

    try {

      const res = await fetch(
        `${API_BASE_URL}/api/trips/share/${shareToken}/copy`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`
          }
        }
      );

      const data = await res.json();

      if (data.travelKeyToken) {

        localStorage.removeItem("redirectAfterLogin");

        navigate(`/trip/${data.travelKeyToken}`);

      }

    } catch (err) {

      console.error(err);
      alert("Failed to save trip");

    } finally {

      setSaving(false);

    }

  };


  /* ================= LOADING ================= */

  if (loading) {

    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-gray-500 text-sm">
          Loading shared itinerary...
        </p>
      </div>
    );

  }


  /* ================= ERROR ================= */

  if (error || !trip) {

    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-red-500 font-medium">
          {error || "Trip not found"}
        </p>
      </div>
    );

  }


  /* ================= DATA ================= */

  const cityData = trip.cities?.[0] || {};
  const cityName = cityData?.cityName || "Trip";
  const itineraryDays = cityData?.itineraryDays || [];


  /* ================= PAGE ================= */

  return (

    <div className="min-h-screen bg-[#fdfcf7] text-gray-900">

      {/* HERO */}

      <div className="max-w-4xl mx-auto px-6 pt-20 pb-12 text-center">

        <h1 className="text-4xl font-bold">
          {cityName} Travel Plan
        </h1>

        <p className="text-gray-500 mt-2">
          Shared via Expeditio
        </p>

      </div>


      {/* ITINERARY */}

      <div className="max-w-3xl mx-auto bg-white border border-gray-100 rounded-3xl p-8 space-y-10">

        <h2 className="text-xl font-bold">
          Itinerary
        </h2>


        {itineraryDays.length === 0 && (
          <p className="text-gray-400 text-sm">
            No itinerary available.
          </p>
        )}


        {itineraryDays.map((day, dayIndex) => (

          <div key={dayIndex} className="space-y-4">

            <h3 className="font-semibold text-lg">
              Day {day.dayNumber}
            </h3>


            {/* If sections exist */}

            {day.sections && day.sections.length > 0 ? (

              day.sections.map((section, sectionIndex) => (

                <div
                  key={sectionIndex}
                  className="pl-4 border-l border-gray-200"
                >

                  <p className="text-sm font-semibold text-gray-500 mb-2">
                    {section.period}
                  </p>

                  {section.activities?.map((activity, activityIndex) => (

                    <div
                      key={activityIndex}
                      className="text-sm text-gray-700 mb-2"
                    >

                      {activity.description}

                      {activity.location && (
                        <span className="text-gray-400 ml-2">
                          • {activity.location}
                        </span>
                      )}

                    </div>

                  ))}

                </div>

              ))

            ) : (

              /* fallback if sections are empty */

              day.activities?.map((activity, activityIndex) => (

                <div
                  key={activityIndex}
                  className="text-sm text-gray-700 mb-2"
                >
                  {activity}
                </div>

              ))

            )}

          </div>

        ))}

      </div>


      {/* SAVE BUTTON */}

      <div className="text-center mt-12 pb-20">

        <button
          onClick={handleSaveTrip}
          disabled={saving}
          className="bg-[#5b6f00] text-white px-8 py-3 rounded-xl font-semibold hover:bg-[#4a5a00] transition disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save to My Trips"}
        </button>

      </div>

    </div>

  );

};

export default PublicTrip;